import React from 'react'
import { Link, useLocation, useNavigation } from 'react-router-dom'

import { Icon, IconNameAndCategoryString } from '../../Icon'
import Badge from './Badge'
import Label from './Label'
import Tooltip from './Tooltip'

export interface NavLink {
    to: string
    label: string
    icon: IconNameAndCategoryString
    disabled?: boolean
    exact?: boolean
    count?: number
}

type NavLinkProps = {
    link: NavLink
    collapsed: boolean
    onClick?: () => void
}

const NavLink = ({ link, collapsed, onClick }: NavLinkProps) => {
    const { state, location } = useNavigation()
    const { pathname } = useLocation()

    const optimisticPathname = state !== 'idle' ? (location?.pathname ?? pathname) : pathname

    const isCurrent = link.exact
        ? optimisticPathname === link.to
        : link.to === '/'
          ? optimisticPathname === link.to ||
            optimisticPathname.startsWith(`/${link.label.toLowerCase()}`)
          : optimisticPathname.startsWith(link.to)

    return (
        <Tooltip label={link.label} disabled={link.disabled || !collapsed}>
            <Link
                to={link.to}
                className={`focus-visible:border-info-400 flex items-center gap-4 rounded-lg border border-white px-4 py-3 no-underline hover:no-underline focus:no-underline focus-visible:border-2 focus-visible:p-[15px] focus-visible:outline-hidden active:bg-white active:bg-linear-to-b active:from-black/8 active:to-black/8 ${
                    isCurrent
                        ? 'text-primary-700 hover:text-primary-700 focus:text-primary-700 bg-white bg-linear-to-b from-black/4 to-black/4'
                        : link.disabled
                          ? 'pointer-events-none opacity-30'
                          : 'text-gray-600 hover:bg-linear-to-b hover:from-black/4 hover:to-black/4 hover:text-gray-600 focus:text-gray-600'
                }`}
                onClick={onClick}
                aria-disabled={link.disabled}
            >
                <span
                    className={`flex size-6 items-center justify-center ${
                        isCurrent ? 'text-primary-700' : 'text-gray-400'
                    }`}
                >
                    <Icon icon={link.icon} shouldInheritColor />
                </span>
                {!collapsed && (
                    <Label className="cursor-pointer" size="sm">
                        {link.label}
                    </Label>
                )}
                {link.count !== undefined && (
                    <Badge
                        value={link.count}
                        className={`pointer-events-none ${collapsed ? 'absolute top-0 right-0' : ''}`}
                    />
                )}
            </Link>
        </Tooltip>
    )
}

export default NavLink
